import styled from "@emotion/styled"
import { useConsole } from "contexts/Console"
import { useMotionValueEvent } from "framer-motion"
import useConstant from "hooks/useConstant"
import { createRef, useEffect, useLayoutEffect, useRef } from "react"
import Pagination from "./Pagination"
import ArticleSimple from "./components/ArticleSimple"
import ArticleOverlay from "./components/ArticleOverlay"
import Cover from "./components/Cover"
import ImageSimple from "./components/ImageSimple"
import Introduction from "./components/Introduction"
import Quote from "./components/Quote"
import QuoteTextEnd from "./components/QuoteTextEnd"
import Push from "./components/Push"
import Roller from "./components/Roller"
import RollerRef from "./components/RollerRef"
import Share from "./components/Share"
import TextImageHorizontal from "./components/TextImageHorizontal"
import TextImageHorizontalPadding from "./components/TextImageHorizontalPadding"
import VideoPlayer from "./components/VideoPlayer"
import { StateProvider, useValue } from "./context"

const Root = styled.div`
  display: grid;

  > div,
  > nav {
    grid-area: 1 / -1;

    > div {
      position: relative;
    }
  }
`

const components = {
  article_overlay: ArticleOverlay,
  article_simple: ArticleSimple,
  cover: Cover,
  image_simple: ImageSimple,
  introduction: Introduction,
  quote: Quote,
  quote_text_end: QuoteTextEnd,
  push: Push,
  text_image_horizontal: TextImageHorizontal,
  text_image_horizontal_padding: TextImageHorizontalPadding,
  video_player: VideoPlayer,
  media_roller: Roller,
  keep_exploring: RollerRef,
}

function Content({ componentsList, children, share }) {
  const ref = useRef()
  const refs = useConstant(() => new Map(Array.from(Array(children.length)).map((_item, i) => [i, createRef()])))

  const { currentIndex, targetSlide, globalProg, inputMap } = useValue()

  function onY(y) {
    const arr = inputMap
      .get()
      .split(",")
      .map(str => Number(str))
    currentIndex.set(arr.findLastIndex(item => item <= y + window.innerHeight / 2))
  }
  useEffect(() => globalProg.onChange(onY))

  function onTargetChange(i) {
    if (i === -1) return
    const arr = inputMap
      .get()
      .split(",")
      .map(str => Number(str))

    const target = arr[i]

    const currentScroll = window.scrollY
    const offset = target > currentScroll ? 80 : 0

    window.scrollTo({
      top: target + offset,
      left: 0,
      behavior: "smooth",
    })
    if (i === 0 || i === arr.length - 2) {
      targetSlide.set(-1)
    }
  }
  useMotionValueEvent(targetSlide, "change", onTargetChange)

  function onscroll() {
    globalProg.set(window.scrollY - ref.current.offsetTop /* - ref.current.offsetParent.offsetTop*/)
  }

  function adjustPos(pos, components_list) {
    const cssVarHeightValue = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--cssVarHeight")) || 0
    const visibleHeight = window.innerHeight - cssVarHeightValue

    return pos.map((p, index) => {
      const adjustment = components_list.slice(0, index).reduce((acc, componentType) => {
        return acc + (["introduction", "quote", "quote_text_end"].includes(componentType) ? visibleHeight : 0)
      }, 0)
      return p - adjustment
    })
  }

  useLayoutEffect(() => {
    const r = ref.current

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          const pos = [...refs].reduce((acc, arr) => acc.concat([acc[acc.length - 1] + arr[1].current.offsetHeight]), [0]).sort((a, b) => a - b)
          const pos_adjusted = adjustPos(pos, componentsList) // TODO: in pos reducer instead

          inputMap.set(pos_adjusted.toString())
        }
      }
    })

    resizeObserver.observe(r)

    return () => resizeObserver.unobserve(r)
  }, [])

  useLayoutEffect(() => {
    window.addEventListener("scroll", onscroll, { passive: true })
    return () => window.removeEventListener("scroll", onscroll, { passive: true })
  }, [])

  return (
    <Root ref={ref}>
      {share && <Share {...share} />}
      <div>
        {children.map((child, i) => (
          <div id={`testimonee--${i}`} key={child.key || i} ref={refs.get(i)} style={{ "--pos": i + 1, zIndex: children.length - i }}>
            {child}
          </div>
        ))}
      </div>
      <Pagination sections={children} />
    </Root>
  )
}

export default function TestimoneeContent({ content, name, share, event }) {
  const console = useConsole()

  const componentsList = content.map(({ component_type }) => component_type)

  console.log("TestimoneeContent(%o)", content)

  return (
    <StateProvider>
      <Content componentsList={componentsList} share={share}>
        {content
          .map(({ component_type, _metadata: { uid }, ...item }) => {
            const Component = components[component_type]

            if (!Component) {
              console.error("TestimoneeContent(%o): unknown component type %o in %o", component_type, content)
              return null
            }

            return <Component key={uid} name={name} event={event} {...item} />
          })
          .filter(Boolean)}
      </Content>
    </StateProvider>
  )
}
